<template>
  <OnBoarding>
    <template>
      <PTransparentLoader :loading="loading">
        <CRow>
          <CCol>
            <OnBoardingHeader :title="pageTitle"/>

            <CAlert show v-if="invalidCode || invitationError" color="danger">
              <translate translate-context="accept.error" v-if="invitationAlreadyAccepted">Invitation already accepted</translate>
              <translate translate-context="accept.error" v-else-if="invalidCode">Invalid invitation code</translate>
              <div class="mt-2 small">
                <translate translate-context="accept.error" v-if="invitationAlreadyAccepted">This invitation has already been accepted and cannot be used again.</translate>
                <translate translate-context="accept.error" v-else>This invitation may have been revoked or the code may be incorrect.</translate>
              </div>
            </CAlert>

            <CRow class="justify-content-center align-items-center text-center" v-if="!invalidCode && !invitationError && invitation">
              <CCol col="12">
                <div class="font-weight-medium"><translate translate-context="accept.invitation">You have been invited by:</translate></div>
                <div class="mb-2">{{ invitation.invited_by ? invitation.invited_by.name || invitation.invited_by.email : '' }}</div>

                <template v-if="invitation.pica_service">
                  <div class="font-weight-medium"><translate translate-context="accept.invitation">Event:</translate></div>
                  <div class="mb-2">{{ invitation.pica_service.name }}</div>
                </template>

                <template v-if="invitation.contract_manager">
                  <div class="font-weight-medium"><translate translate-context="accept.invitation">Contract Manager:</translate></div>
                  <div class="mb-2">{{ invitation.contract_manager.name }}</div>
                </template>

                <template v-if="invitation.scope_type === CollaboratorScope.GLOBAL">
                  <div class="font-weight-medium"><translate translate-context="accept.invitation">Global Access</translate></div>
                  <div class="mb-2"><translate translate-context="accept.invitation">You will have access to the entire system</translate></div>
                </template>

                <template v-if="invitation.roles && invitation.roles.length">
                  <div class="font-weight-medium"><translate translate-context="accept.invitation">Roles:</translate></div>
                  <div class="mb-3" v-for="role in invitation.roles" :key="role.id">
                    {{ role.name }}
                    <span v-if="role.expires_at" class="text-muted">
                      (<translate translate-context="accept.invitation">expires</translate> {{ new Date(role.expires_at).toLocaleDateString() }})
                    </span>
                  </div>
                </template>
              </CCol>

              <CCol col="12">
                <!-- Utente autenticato -->
                <template v-if="isAuthenticated">
                  <!-- Email match -->
                  <template v-if="emailMatches">
                    <div class="font-weight-semi-bold mb-4 text-info">
                      <translate translate-context="accept.logged">You are logged in as:</translate>
                      {{ user.email }}
                    </div>
                    <CButton @click="goToInvitations" block color="primary" class="mb-2">
                      <translate translate-context="accept.button">View and accept invitation</translate>
                    </CButton>
                  </template>

                  <!-- Email mismatch -->
                  <template v-else>
                    <CAlert color="warning" class="text-left">
                      <div class="font-weight-semi-bold mb-2">
                        <CIcon name="cilWarning" class="mr-1"/>
                        <translate translate-context="accept.warning">Account mismatch</translate>
                      </div>
                      <div class="small">
                        <translate
                          translate-context="accept.warning"
                          :translate-params="{ inviteEmail: invitationEmail, loggedEmail: user.email }"
                        >This invitation is for %{ inviteEmail }, but you are logged in as %{ loggedEmail }.</translate>
                      </div>
                    </CAlert>

                    <CButton @click="goToInvitations" block color="primary" class="mb-2">
                      <translate translate-context="accept.button">Accept with this account</translate>
                      ({{ user.email }})
                    </CButton>

                    <CButton @click="doLogout" color="secondary" block>
                      <translate translate-context="accept.button">Change account</translate>
                    </CButton>
                  </template>
                </template>

                <!-- Utente non autenticato -->
                <template v-else>
                  <!-- Token input phase -->
                  <template v-if="showTokenInput">
                    <div class="text-center mb-4" style="font-size: 16px">
                      <translate translate-context="accept.token">A verification code has been sent to:</translate><br>
                      <span class="font-weight-semi-bold">{{ email }}</span>
                    </div>

                    <TokenInput
                      :validating="validatingToken"
                      :error-message="tokenError"
                      :show-resend="true"
                      @complete="onTokenComplete"
                      @resend="resendToken"
                      ref="tokenInput"
                    />

                    <div class="text-center mt-3">
                      <CLink @click="resetForm" class="text-muted small">
                        <translate translate-context="accept.form">Use a different email</translate>
                      </CLink>
                    </div>
                  </template>

                  <!-- Email input phase -->
                  <template v-else>
                    <CInput
                      ref="emailInput"
                      :placeholder="$pgettext('auth.placeholder','E-mail')"
                      v-model="v$.email.$model"
                      :label="$gettext('E-mail')"
                      :is-valid="!v$.email.$error && !emailError"
                      autocomplete="username email"
                      :disabled="showTermsInput"
                      @keyup.enter="requestAccess">
                      <template v-slot:invalid-feedback>
                        <div class="invalid-feedback d-block" v-for="error of v$.email.$errors" :key="error.$uid">
                          <div class="error-msg">{{ error.$message }}</div>
                        </div>
                        <div class="invalid-feedback d-block" v-if="emailError">
                          <div class="error-msg">{{ emailError }}</div>
                        </div>
                      </template>
                    </CInput>

                    <!-- Terms - mostrati solo per nuovi utenti -->
                    <template v-if="showTermsInput">
                      <div class="d-flex mb-3 text-left">
                        <CInputCheckbox custom
                                        :checked.sync="v$.termsAgreement.$model"
                                        :is-valid="!v$.termsAgreement.$error"
                                        id="termsAgreement"
                                        :placeholder="$pgettext('auth.placeholder','Terms and conditions')">
                        </CInputCheckbox>
                        <div>
                          <label for="termsAgreement" class="form-label mb-0" v-html="termsAgreementText"/>
                          <div class="invalid-feedback d-block" v-for="error of v$.termsAgreement.$errors" :key="error.$uid">
                            <div class="error-msg" v-translate translate-context="auth.terms.error">You must accept the terms and conditions</div>
                          </div>
                        </div>
                      </div>
                    </template>

                    <PButton :loading="submitting" type="button" color="primary" block @click="requestAccess">
                      <translate translate-context="accept.button">Continue</translate>
                    </PButton>

                    <!-- Link per cambiare email quando si è in fase terms -->
                    <div class="text-center mt-3" v-if="showTermsInput">
                      <CLink @click="resetToEmailInput" class="text-muted small">
                        <translate translate-context="accept.form">Use a different email</translate>
                      </CLink>
                    </div>

                    <div class="text-center info mt-3">
                      <span v-translate translate-context="accept.info">or</span>
                    </div>

                    <SocialLogin can-register @login-success="afterLogin"/>

                    <CAlert v-if="error" color="danger" :show="!!error" class="mt-3">
                      {{ error }}
                    </CAlert>
                  </template>
                </template>
              </CCol>
            </CRow>
          </CCol>
        </CRow>
      </PTransparentLoader>
    </template>
  </OnBoarding>
</template>

<script>
import PTransparentLoader from "@/domain/core/components/PTransparentLoader.vue";
import SocialLogin from "@/domain/login/components/providers/SocialLoginButtons.vue";
import TokenInput from "@/domain/login/components/TokenInput.vue";
import OnBoarding from "@/domain/login/OnBoarding.vue";
import OnBoardingHeader from "@/domain/login/components/OnBoardingHeader.vue";
import PButton from "@/components/PButton.vue";
import {mapActions, mapGetters} from "vuex";
import {useVuelidate} from "@vuelidate/core";
import {email$, required$, sameAs$} from "@/domain/core/utils/validators";
import CollaboratorScope from "@/domain/core/constant/collaboratorScope";
import {methods} from "@/domain/login/mixin";

export default {
  name: 'Accept',
  components: {OnBoardingHeader, OnBoarding, PTransparentLoader, SocialLogin, TokenInput, PButton},

  setup() {
    return {v$: useVuelidate()}
  },

  data() {
    return {
      loading: true,
      submitting: false,
      error: null,
      emailError: null,
      invitationError: null,
      CollaboratorScope,
      email: '',
      termsAgreement: false,
      showTokenInput: false,
      showTermsInput: false, // Mostrato solo per nuovi utenti
      validatingToken: false,
      tokenError: null
    }
  },

  validations() {
    // Terms richiesti solo quando showTermsInput è true
    const rules = {
      email: {required$, email$}
    }
    if (this.showTermsInput) {
      rules.termsAgreement = {sameAs: sameAs$(true)}
    }
    return rules
  },

  computed: {
    ...mapGetters('login', ['isAuthenticated', 'user', 'invitation', 'registrationFlow', 'userLanguage']),

    invalidCode() {
      return !this.invitation && this.$route.params.code && !this.loading
    },

    invitationAlreadyAccepted() {
      return this.invitationError === 'invalid_collaborator_status'
    },

    invitationEmail() {
      return this.$route.query.email || this.invitation?.email || ''
    },

    emailMatches() {
      if (!this.user || !this.invitationEmail) return true
      return this.user.email.toLowerCase() === this.invitationEmail.toLowerCase()
    },

    pageTitle() {
      if (this.invitationAlreadyAccepted) {
        return this.$pgettext('accept.title', 'Invitation already used')
      }
      if (this.invalidCode || this.invitationError) {
        return this.$pgettext('accept.title', 'Invalid invitation')
      }
      return this.$pgettext('accept.title', 'Accept invitation')
    },

    termsAgreementText() {
      const translatedText = this.$gettext("I declare that I am at least 18 years old and have read and agree to the [TERMS_LINK] and the [PRIVACY_LINK] of GetPica.");

      const termsLink = `<a href="${this.policyUrl.terms}" target="_blank">${this.$gettext('Terms and Conditions')}</a>`;
      const privacyLink = `<a href="${this.policyUrl.privacy}" target="_blank">${this.$gettext('Privacy Policy')}</a>`;

      return translatedText
        .replace('[TERMS_LINK]', termsLink)
        .replace('[PRIVACY_LINK]', privacyLink);
    },

    policyUrl() {
      return {
        terms: `https://policy.getpica.com/${this.userLanguage}/termini-condizioni-vendita/`,
        privacy: `https://policy.getpica.com/${this.userLanguage}/privacy-policy-pica/`,
      }
    }
  },

  async mounted() {
    try {
      await this.loadInvitation()
    } catch (e) {
      if (e.data?.code) {
        this.invitationError = e.data.code
      }
    } finally {
      this.loading = false
    }

    // Se non c'è invito e nessun code nell'URL, vai al login
    if (!this.invitation && !this.$route.params.code) {
      await this.$router.push({name: 'login'})
      return
    }

    // Pre-popola l'email dall'invito
    if (this.invitationEmail) {
      this.email = this.invitationEmail
    }
  },

  methods: {
    ...methods,
    ...mapActions('login', ['previewInvitation', 'logout', 'createUser', 'validateCode', 'sendLoginToken', 'resendValidationCode', 'authenticateWithToken']),
    ...mapActions('invitations', ['fetchInvitationByCode']),

    async loadInvitation() {
      const code = this.$route.params.code
      const email = this.$route.query.email || ''

      if (!code) return

      // Carica preview dell'invito (usa l'endpoint esistente nello store login)
      await this.previewInvitation({ code, email })

      // Salva anche nello store invitations per il flusso successivo
      // Questo salverà in sessionStorage per persistere durante login
      try {
        await this.fetchInvitationByCode({ code, email })
      } catch (e) {
        // Se fallisce, non è critico - l'invito è già nello store login
      }
    },

    async requestAccess() {
      if (!await this.v$.$validate()) return

      this.submitting = true
      this.error = null
      this.emailError = null

      try {
        if (this.showTermsInput) {
          // Fase 2: utente nuovo, ha accettato i terms → registra
          await this.createUser({ email: this.email })
          this.showTokenInput = true
          this.showTermsInput = false
          this.tokenError = null
        } else {
          // Fase 1: prova login token (per utenti esistenti)
          await this.sendLoginToken({ email: this.email })
          this.showTokenInput = true
          this.tokenError = null
        }
      } catch (e) {
        const errorCode = e.data?.code

        if (errorCode === 'user_not_found_for_login_token') {
          // Utente non trovato → nuovo utente, mostra terms
          this.showTermsInput = true
          this.error = null
        } else if (await this.manageLoginError(e, this.email)) {
          return
        } else {
          this.error = this.$gettext('An error occurred. Please try again.')
        }
      } finally {
        this.submitting = false
      }
    },

    async onTokenComplete(token) {
      this.validatingToken = true

      try {
        // Usa endpoint diverso in base al flow
        if (this.registrationFlow === 'validation_required') {
          // Nuovo utente - valida registration token
          await this.validateCode({ email: this.email, code: token })
        } else {
          // Utente esistente - login con token
          await this.authenticateWithToken({ email: this.email, token })
        }

        await this.afterLogin()
      } catch (e) {
        let errorMsg = this.$pgettext('login.error', 'Invalid code')

        if (e.data?.code === 'invalid_login_token' || e.data?.code === 'invalid_registration_token') {
          errorMsg = this.$pgettext('login.error', 'Invalid or expired code')
        }

        this.tokenError = errorMsg
      } finally {
        this.validatingToken = false
      }
    },

    async resendToken() {
      try {
        if (this.registrationFlow === 'validation_required') {
          await this.resendValidationCode({ email: this.email })
        } else {
          await this.sendLoginToken({ email: this.email })
        }
        this.tokenError = null
      } catch (e) {
        // Per sicurezza, non mostrare errori specifici
        this.tokenError = null
      }
    },

    resetForm() {
      this.showTokenInput = false
      this.showTermsInput = false
      this.tokenError = null
      this.emailError = null
      this.error = null
      this.termsAgreement = false
      this.v$.$reset()

      this.$nextTick(() => {
        if (this.$refs.emailInput) {
          this.$refs.emailInput.$el.children[0].focus()
        }
      })
    },

    resetToEmailInput() {
      this.showTermsInput = false
      this.termsAgreement = false
      this.error = null
      this.v$.$reset()

      this.$nextTick(() => {
        if (this.$refs.emailInput) {
          this.$refs.emailInput.$el.children[0].focus()
        }
      })
    },

    async doLogout() {
      await this.logout()
      // Dopo logout, rimani sulla stessa pagina per rifare login
    },

    goToInvitations() {
      const code = this.$route.params.code
      this.$router.push({
        name: 'invitations.list',
        query: code ? { code } : undefined
      })
    }
  }
}
</script>
